import HistoryIcon from '@mui/icons-material/History';
import { Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { changeDate } from '../../../api/orders';
import { useTabs } from '../../../context/TabContext';
import { getPendingOrders, updateOrderSelectType, updateSelectedPendingOrders } from '../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../redux/store';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import ManagerChipsWrapper from '../../../reusable-components/datagrid/ManagerChipsWrapper';
import { PENDING_ORDERS } from '../../../reusable-components/datagrid/orderColumns';
import { useSnackbar } from '../../../reusable-components/snackbar';
import { fDate } from '../../../utils/formatTime';
import CustomToolbar from './CustomToolbar';
import DeleteOrderModalButton from './DeleteOrderModalButton';
import OrderNotesViewModal from './OrderNotesViewModal';
import ReviewColumnIcons from './ReviewColumnIcons';
import { markMetricRemoved, NotificationMetricType } from '../../../redux/slices/notifications';
import { NotificationIdsChip, useNotificationIdsFilter } from '../../../hooks/useNotificationIdsFilter';

export default function PendingOrdersView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { matchesNotificationIds, isNotificationLoading } = useNotificationIdsFilter([NotificationMetricType.OrdersNeedApproval, NotificationMetricType.OrdersSubmitted]);
  const [parentFacilityId, setParentFacilityId] = useState('');
  const [submittingCombiningOrders, setSubmittingCombiningOrders] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { addNewTabAndNavigate } = useTabs();

  const [data, setData] = useState([]);
  const [activeRows, setActiveRows] = useState([]);

  const { orderSelectType, selectedPendingOrders, data: { pendingOrders } } = useSelector((state) => state.orders);
  const isLoading = useSelector((state) => state.orders.isLoading);

  useEffect(() => {
    setData((pendingOrders ?? []).filter((ord) => matchesNotificationIds(ord.id)).map(ord => ({ ...ord, actions: [ord.messageCount ? "Notes" : "", ord.orderDelay ? "Delayed Order" : ""].filter(val => val).join(", ") })));
  }, [searchParams, pendingOrders, matchesNotificationIds]);

  const customToolbar = useCallback(() => {
    const isFiltered = (filter) => filter === searchParams.get('filter');
    return (
      <CustomToolbar
        onStartCombineOrders={() => dispatch(updateOrderSelectType('Combine Orders'))}
        onCombineOrdersCancel={() => {
          dispatch(updateSelectedPendingOrders([]));
          dispatch(updateOrderSelectType(''));
          setParentFacilityId('');
        }}
        submittingCombiningOrders={submittingCombiningOrders}
        setSubmittingCombiningOrders={setSubmittingCombiningOrders}
        isFiltered={isFiltered}
        activeRows={activeRows}
      />
    );
  }, [searchParams, activeRows]);

  const changeOrderDate = async (id, orderDate) => {
    if (orderDate === null) return;
    const date = orderDate.toLocaleString();
    const response = await changeDate(id, date);
    if (response.data) {
      dispatch(getPendingOrders());
      enqueueSnackbar('Order date changed successfully', { variant: 'success' });
    }
    else enqueueSnackbar('Error Changing Date', { variant: 'error' });
  }

  const ActionButtons = (params) => {
    const { orderNumber, id, locked } = params.row;
    return (
      <Stack direction="row" spacing={0.3}>
        {params.row.orderDelay && <OrderDelay params={params} />}
        <OrderNotesViewModal order={params.row} />
        <DeleteOrderModalButton
          disabled={locked}
          content={`Are you sure you want to delete ${orderNumber}?`}
          tooltip={`Delete Order${locked ? ' (Permission Required) ' : ''}`}
          rowId={id}
          disableRipple
          action={() => {
            dispatch(markMetricRemoved(NotificationMetricType.OrdersSubmitted, id));
            dispatch(markMetricRemoved(NotificationMetricType.OrdersNeedApproval, id));
            dispatch(getPendingOrders());
          }}
        />
      </Stack>
    );
  };

const OrderDelay = ({ params }) => {
  const [orderDate, setOrderDate] = useState(null);

  const sendDate = new Date(params.row.orderDelay.sendOrderAt);
  const now = new Date();
  const isPast = sendDate <= now;

  const tooltip = (
    <div>
      Order was set on a timer by {params.row.orderDelay.addedBy} on{' '}
      {fDate(params.row.orderDelay.dateAdded, 'MM/dd/yyyy')}
      <br />
      Order {isPast ? 'was' : 'will be'} sent out on
      {fDate(params.row.orderDelay.sendOrderAt, 'MM/dd/yyyy hh:mm a')}
    </div>
  );

  return params.row.submitLocked ? (
    <Tooltip arrow title={tooltip}>
      <IconButton
        onClick={(e) => e.stopPropagation()}
        color="warning"
        disableRipple
        sx={{ p: 0, cursor: 'default' }}
      >
        <HistoryIcon />
      </IconButton>
    </Tooltip>
  ) : (
    <ConfirmDialog
      icon="history-outline"
      title="Change Order Date"
      color="warning"
      tooltip={tooltip}
      content={
        <DateTimePicker
          minDate={new Date()}
          slotProps={{
            textField: { size: 'small', sx: { width: '100%' } },
            actionBar: { actions: ['clear', 'cancel', 'accept'] },
          }}
          value={orderDate || new Date(params.row.orderDelay.sendOrderAt)}
          onChange={(val) => setOrderDate(val)}
        />
      }
      actionButton="Change"
      action={() => changeOrderDate(params.row.id, orderDate)}
      cancelAction={() => setOrderDate(null)}
      disableRipple
    />
  );
};

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 115,
    type: 'customSelect',
    renderCell: (params) => ActionButtons(params),
  };

  const manager = ManagerChipsWrapper(() => dispatch(getPendingOrders()), 'Orders');

  const reviewer = {
    field: 'reviews',
    headerName: 'Reviewer',
    type: 'customSelect',
    minWidth: 120,
    flex: 1.5,
    customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
    getFilterValues: (row) => row.reviews?.map((r) => r.reviewerName) ?? [],
    valueGetter: (params) => params.row.reviews?.map((r) => r.reviewerName).join(', '),
    renderCell: (params) => <ReviewColumnIcons params={params} pending />,
  };

  const columns = useMemo(() => [...PENDING_ORDERS, reviewer, manager, actions], []);

    const rowClick = (id) => {
    navigate(`/dashboard/orders/${id}?filter=pending`);
    };

  return (
    <Grid item xs={12}>
      <NotificationIdsChip label="orders" />
      <CustomDataGrid
        gridId="pending-orders-view"
        applyQuickFilter
        data={data}
        gridColumns={columns}
              defaultHiddenColumns={['label','department', 'group', 'vendorShipDate', 'itemCount', 'total', 'ticketIds', 'reviews']}
        getRowId={(row) => row.id}
        onRowClick={(params, e) => {
          const selection = window.getSelection().toString();
          if (selection.length > 0) return;
          if (e.deleteChip || e.addChip || e.openTicket || e.combine || e.confirmDialog) return;
          if (e.navigateToTickets) {
            addNewTabAndNavigate(`/dashboard/tickets/${e.navigateToTickets}`);
            return;
          }
          rowClick(params.row.id);
        }}
        disableRowSelectionOnClick
        disableCellHighlight
        checkboxSelection={orderSelectType.length > 0}
        CustomLeftToolbar={customToolbar}
        rowSelectionModel={selectedPendingOrders}
        checkboxTooltipText="Permission Required"
        checkboxTooltipCheck={(row) => row.locked}
        isRowSelectable={(params) => (orderSelectType === 'Combine Orders' || orderSelectType === 'Select Orders to Process') && !params.row.locked}
        onRowSelectionModelChange={(newSelection) => {
          if (orderSelectType === 'Combine Orders') {
            if (!newSelection || newSelection.length === 0) {
              setParentFacilityId('');
              dispatch(updateSelectedPendingOrders([]));
              return;
            }
            if (!data) return;
            const filteredData = data.filter(row => newSelection.includes(row.id));
            const lastSelected = filteredData.find(row => row.id === newSelection[newSelection.length - 1]);
            const combineFacilityId = parentFacilityId || lastSelected.facilityId;
            if (filteredData.some(row => row.facilityId !== combineFacilityId)) {
              enqueueSnackbar("Can't combine an order from a different facility", { variant: 'error' });
              return;
            }
            if (!parentFacilityId) setParentFacilityId(lastSelected.facilityId);
            dispatch(updateSelectedPendingOrders(newSelection));
          } else if (orderSelectType === 'Select Orders to Process') {
            dispatch(updateSelectedPendingOrders(newSelection));
          }
        }}
        isLoading={isLoading || isNotificationLoading}
        setActiveRows={setActiveRows}
        advancedFilter
        advancedSearchBase={'pendingOrders'}
      />
    </Grid>
  );
}
